<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import type { Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { BookOpen, Building2 } from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';
import {
  TRAINING_CENTRE_LOCATION_FILTER_ALL,
  collectDistrictFilterOptionsFromCentres,
  collectSubdistrictFilterOptionsForDistrict,
  programRowOfferedWithLocationAdminFilters,
  programRowCardTitle,
  trainingCentreLocationHierarchyLine,
  trainingCentreLocationMatchesAdminFilters,
  type ProgramAccreditationLevelRow,
  type ProgramRow,
  type TrainingCentreRow,
  uniqueProgramsFromTrainingCentres,
} from '@/db/trainingCentres';
import { programAccreditationLevelLabel } from '@/utils/programAccreditationLevel';
import TrainingCentresDirectoryMap from '@/components/TrainingCentresDirectoryMap.vue';
import { useContentStore } from '@/stores/content';
import { useTrainingCentresStore } from '@/stores/trainingCentres';

const ALL = TRAINING_CENTRE_LOCATION_FILTER_ALL;

const SearchIcon = (LucideIcons as Record<string, Component>).Search;
const ArrowRightIcon = (LucideIcons as Record<string, Component>).ArrowRight;
const MapPinIcon = (LucideIcons as Record<string, Component>).MapPin;
const ChevronDownIcon = (LucideIcons as Record<string, Component>).ChevronDown;

const props = defineProps<{
  filter_card_title?: string | null;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const trainingCentresStore = useTrainingCentresStore();

const programDirectoryMapRef = ref<InstanceType<typeof TrainingCentresDirectoryMap> | null>(null);
const programDirectoryMapMounted = ref(false);

const searchQuery = ref('');
const selectedDistrict = ref(ALL);
const selectedSubdistrict = ref(ALL);
const selectedLevelId = ref(ALL);

watch(selectedDistrict, () => {
  selectedSubdistrict.value = ALL;
});

onMounted(() => {
  void trainingCentresStore.initialize();
});

function levelDisplayName(level: ProgramAccreditationLevelRow | null): string {
  if (!level) return '';
  return programAccreditationLevelLabel(level.roman_numeral, t);
}

function levelBadgeModifier(level: ProgramAccreditationLevelRow | null): string {
  const r = (level?.roman_numeral || '').trim();
  if (r === 'IV') return 'program-list-block__badge--purple';
  if (r === 'III' || r === 'II') return 'program-list-block__badge--blue';
  if (r === 'I') return 'program-list-block__badge--green';
  return 'program-list-block__badge--neutral';
}

/** Per program: training centres and distinct program accreditation levels. */
const programIndex = computed(() => {
  const map = new Map<
    number,
    {
      trainingCentreNames: Set<string>;
      levels: Map<number, ProgramAccreditationLevelRow>;
    }
  >();
  for (const centre of trainingCentresStore.items) {
    const centreName = (centre.name || '').trim();
    for (const link of centre.programs ?? []) {
      const p = link.program;
      if (!p) continue;
      let entry = map.get(p.id);
      if (!entry) {
        entry = {
          trainingCentreNames: new Set(),
          levels: new Map(),
        };
        map.set(p.id, entry);
      }
      if (centreName) entry.trainingCentreNames.add(centreName);
      const lvl = link.accreditation_level;
      if (lvl) entry.levels.set(lvl.id, lvl);
    }
  }
  return map;
});

const allPrograms = computed(() => uniqueProgramsFromTrainingCentres(trainingCentresStore.items));

const districtFilterOptions = computed(() =>
  collectDistrictFilterOptionsFromCentres(trainingCentresStore.items)
);

const subdistrictFilterOptions = computed(() =>
  collectSubdistrictFilterOptionsForDistrict(
    trainingCentresStore.items,
    selectedDistrict.value
  )
);

const levelOptions = computed(() => {
  const map = new Map<number, ProgramAccreditationLevelRow>();
  for (const centre of trainingCentresStore.items) {
    for (const link of centre.programs ?? []) {
      const lvl = link.accreditation_level;
      if (lvl) map.set(lvl.id, lvl);
    }
  }
  return [...map.values()].sort((a, b) => {
    const sa = a.sort_order ?? 999;
    const sb = b.sort_order ?? 999;
    if (sa !== sb) return sa - sb;
    return levelDisplayName(a).localeCompare(levelDisplayName(b), undefined, {
      sensitivity: 'base',
    });
  });
});

const filteredProgramCards = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  const levelId = selectedLevelId.value;

  const filtered = allPrograms.value.filter((prog) => {
    const idx = programIndex.value.get(prog.id);
    if (!idx) return false;

    if (q) {
      const name = (prog.name || '').toLowerCase();
      const kodigu = (prog.kodigu || '').toLowerCase();
      const titleLine = programRowCardTitle(prog).toLowerCase();
      const centreMatch = [...idx.trainingCentreNames].some((n) =>
        n.toLowerCase().includes(q)
      );
      if (
        !name.includes(q) &&
        !kodigu.includes(q) &&
        !titleLine.includes(q) &&
        !centreMatch
      ) {
        return false;
      }
    }
    if (
      !programRowOfferedWithLocationAdminFilters(
        trainingCentresStore.items,
        prog.id,
        selectedDistrict.value,
        selectedSubdistrict.value
      )
    ) {
      return false;
    }
    if (levelId !== ALL) {
      const id = Number(levelId);
      if (!idx.levels.has(id)) return false;
    }
    return true;
  });

  return filtered
    .map((prog) => {
      const idx = programIndex.value.get(prog.id)!;
      const centreNames = [...idx.trainingCentreNames].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
      return {
        prog,
        centreNames,
        centreCount: centreNames.length,
      };
    })
    .sort((a, b) => {
      if (b.centreCount !== a.centreCount) return b.centreCount - a.centreCount;
      return programRowCardTitle(a.prog).localeCompare(programRowCardTitle(b.prog), undefined, {
        sensitivity: 'base',
      });
    });
});

const filteredProgramIds = computed(
  () => new Set(filteredProgramCards.value.map(({ prog }) => prog.id))
);

const programListDirectoryMapPins = computed(() => {
  type Pin = {
    lat: number;
    lng: number;
    title: string;
    subtitle?: string;
    href: string;
  };
  const ids = filteredProgramIds.value;
  if (ids.size === 0) return [] as Pin[];

  const d = selectedDistrict.value;
  const s = selectedSubdistrict.value;
  const levelId = selectedLevelId.value;
  const out: Pin[] = [];

  for (const org of trainingCentresStore.items) {
    const relevant = (org.programs ?? []).some((link) => {
      const p = link.program;
      if (!p || !ids.has(p.id)) return false;
      if (levelId !== ALL) {
        const lid = Number(levelId);
        if (!link.accreditation_level || link.accreditation_level.id !== lid) return false;
      }
      return programRowOfferedWithLocationAdminFilters([org], p.id, d, s);
    });
    if (!relevant) continue;

    const withPoint = (org.locations ?? []).filter((loc) => {
      const p = loc.point;
      if (p == null || typeof p.lat !== 'number' || typeof p.lng !== 'number') return false;
      return Number.isFinite(p.lat) && Number.isFinite(p.lng);
    });
    const locForPin =
      withPoint.find((loc) => trainingCentreLocationMatchesAdminFilters(loc, d, s)) ??
      (d === ALL && s === ALL ? withPoint[0] : undefined);
    if (!locForPin?.point) continue;

    out.push({
      lat: locForPin.point.lat,
      lng: locForPin.point.lng,
      title: (org.name || '').trim() || t('trainingCentreDetail.mapPinFallback'),
      subtitle: trainingCentreLocationHierarchyLine(locForPin) || undefined,
      href: router.resolve(detailsLinkToCentre(org)).href,
    });
  }
  return out;
});

function levelsForProgram(programId: number): ProgramAccreditationLevelRow[] {
  const idx = programIndex.value.get(programId);
  if (!idx) return [];
  return [...idx.levels.values()].sort((a, b) => {
    const sa = a.sort_order ?? 999;
    const sb = b.sort_order ?? 999;
    if (sa !== sb) return sa - sb;
    return levelDisplayName(a).localeCompare(levelDisplayName(b), undefined, {
      sensitivity: 'base',
    });
  });
}

const filterCardTitle = computed(() => {
  const custom = (props.filter_card_title || '').trim();
  return custom || t('programList.filterHeading');
});

function routeObjectForHash(hash: string) {
  return {
    path: route.path,
    query: route.query,
    hash: `#${hash}`,
  };
}

function detailsLinkTo(prog: ProgramRow) {
  const pid = prog.detail_page_id;
  if (typeof pid === 'number' && pid > 0) {
    const url = contentStore.getUrlForPageId(pid);
    if (url && url !== '/') return url;
  }
  return routeObjectForHash(`program-${prog.id}`);
}

function detailsLinkToCentre(org: TrainingCentreRow) {
  const pid = org.detail_page_id;
  if (typeof pid === 'number' && pid > 0) {
    const url = contentStore.getUrlForPageId(pid);
    if (url && url !== '/') return url;
  }
  return routeObjectForHash(`training-centre-${org.id}`);
}

function onProgramDirectoryMapToggle(ev: Event) {
  const el = ev.target as HTMLDetailsElement;
  if (el.open) {
    if (!programDirectoryMapMounted.value) programDirectoryMapMounted.value = true;
    nextTick(() => {
      requestAnimationFrame(() => programDirectoryMapRef.value?.relayout());
    });
  }
}
</script>

<template>
  <section
    class="program-list-block"
    :aria-label="t('programList.sectionAria')"
  >
    <div class="program-list-block__filter-card">
      <h2 class="program-list-block__filter-heading">
        <SearchIcon class="program-list-block__filter-icon" :size="20" aria-hidden="true" />
        {{ filterCardTitle }}
      </h2>

      <div class="program-list-block__filters">
        <div class="program-list-block__field">
          <label class="program-list-block__label" for="program-list-search">{{
            t('programList.search')
          }}</label>
          <div class="program-list-block__search-wrap">
            <SearchIcon
              class="program-list-block__search-icon"
              :size="16"
              aria-hidden="true"
            />
            <input
              id="program-list-search"
              v-model="searchQuery"
              type="search"
              class="program-list-block__input program-list-block__input--search"
              :placeholder="t('programList.searchPlaceholder')"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="program-list-block__field">
          <label class="program-list-block__label" for="program-list-district">{{
            t('programList.district')
          }}</label>
          <div class="program-list-block__select-wrap">
            <select
              id="program-list-district"
              v-model="selectedDistrict"
              class="program-list-block__select"
            >
              <option :value="ALL">{{ t('programList.all') }}</option>
              <option
                v-for="opt in districtFilterOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <ChevronDownIcon class="program-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>

        <div class="program-list-block__field">
          <label class="program-list-block__label" for="program-list-subdistrict">{{
            t('programList.subdistrict')
          }}</label>
          <div class="program-list-block__select-wrap">
            <select
              id="program-list-subdistrict"
              v-model="selectedSubdistrict"
              class="program-list-block__select"
              :disabled="selectedDistrict === ALL"
            >
              <option :value="ALL">{{ t('programList.allSubdistricts') }}</option>
              <option
                v-for="opt in subdistrictFilterOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <ChevronDownIcon class="program-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>

        <div class="program-list-block__field">
          <label class="program-list-block__label" for="program-list-level">{{
            t('programList.level')
          }}</label>
          <div class="program-list-block__select-wrap">
            <select id="program-list-level" v-model="selectedLevelId" class="program-list-block__select">
              <option :value="ALL">{{ t('programList.all') }}</option>
              <option v-for="lvl in levelOptions" :key="lvl.id" :value="String(lvl.id)">
                {{ levelDisplayName(lvl) }}
              </option>
            </select>
            <ChevronDownIcon class="program-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>
      </div>

      <p class="program-list-block__count" role="status">
        {{
          t('programList.showingCount', {
            shown: filteredProgramCards.length,
            total: allPrograms.length,
          })
        }}
      </p>
      <p v-if="trainingCentresStore.status === 'loading' && trainingCentresStore.items.length === 0" class="program-list-block__hint">
        {{ t('programList.loading') }}
      </p>
      <p v-else-if="trainingCentresStore.status === 'error' && trainingCentresStore.items.length === 0" class="program-list-block__hint program-list-block__hint--error">
        {{ trainingCentresStore.error || t('programList.loadError') }}
      </p>
    </div>

    <details
      v-if="programListDirectoryMapPins.length > 0"
      class="program-list-block__map-details"
      :aria-label="t('programList.directoryMapSummaryAria')"
      @toggle="onProgramDirectoryMapToggle"
    >
      <summary class="program-list-block__map-summary">
        <MapPinIcon class="program-list-block__map-summary-icon" :size="18" aria-hidden="true" />
        <span class="program-list-block__map-summary-label">{{ t('programList.directoryMapTitle') }}</span>
        <span class="program-list-block__map-summary-count">({{ programListDirectoryMapPins.length }})</span>
        <ChevronDownIcon class="program-list-block__map-chevron" :size="18" aria-hidden="true" />
      </summary>
      <div class="program-list-block__map-panel">
        <TrainingCentresDirectoryMap
          v-if="programDirectoryMapMounted"
          ref="programDirectoryMapRef"
          :pins="programListDirectoryMapPins"
          :canvas-aria-label="t('programList.directoryMapCanvasAria')"
        />
      </div>
    </details>

    <div class="program-list-block__grid">
      <RouterLink
        v-for="{ prog, centreNames, centreCount } in filteredProgramCards"
        :id="`program-${prog.id}`"
        :key="prog.id"
        :to="detailsLinkTo(prog)"
        class="program-list-block__card"
        :aria-labelledby="`program-list-card-title-${prog.id}`"
      >
        <div class="program-list-block__card-top">
          <div class="program-list-block__avatar" aria-hidden="true">
            <BookOpen :size="24" />
          </div>
          <div class="program-list-block__badges">
            <span
              v-for="lvl in levelsForProgram(prog.id)"
              :key="lvl.id"
              class="program-list-block__badge"
              :class="levelBadgeModifier(lvl)"
            >
              {{ levelDisplayName(lvl) }}
            </span>
          </div>
        </div>

        <h3 :id="`program-list-card-title-${prog.id}`" class="program-list-block__card-title">
          {{ programRowCardTitle(prog) }}
        </h3>

        <div
          v-if="centreCount > 0"
          class="program-list-block__meta"
        >
          <div
            class="program-list-block__meta-row"
            :aria-label="`${t('programList.centresOfferingAria')}: ${centreNames.join(', ')}`"
          >
            <Building2 class="program-list-block__meta-icon" :size="16" aria-hidden="true" />
            <span class="program-list-block__meta-text">{{
              centreCount === 1
                ? t('programList.trainingCentresCountOne')
                : t('programList.trainingCentresCountMany', { count: centreCount })
            }}</span>
          </div>
        </div>

        <div class="program-list-block__details-wrap">
          <span class="program-list-block__details">
            {{ t('programList.viewDetails') }}
            <ArrowRightIcon class="program-list-block__details-arrow" :size="16" aria-hidden="true" />
          </span>
        </div>
      </RouterLink>
    </div>

    <div
      v-if="
        filteredProgramCards.length === 0 &&
        trainingCentresStore.status !== 'loading' &&
        allPrograms.length > 0
      "
      class="program-list-block__empty"
    >
      <div class="program-list-block__empty-icon-wrap" aria-hidden="true">
        <SearchIcon :size="32" />
      </div>
      <h3 class="program-list-block__empty-title">{{ t('programList.noResultsTitle') }}</h3>
      <p class="program-list-block__empty-text">{{ t('programList.noResultsHint') }}</p>
    </div>

    <div
      v-if="
        allPrograms.length === 0 &&
        trainingCentresStore.status !== 'loading' &&
        trainingCentresStore.status !== 'uninitialized'
      "
      class="program-list-block__empty"
    >
      <p class="program-list-block__empty-text">{{ t('programList.noPrograms') }}</p>
    </div>
  </section>
</template>

<style scoped>
.program-list-block {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.program-list-block__filter-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .program-list-block__filter-card {
    border-radius: var(--radius-lg);
  }
}

.program-list-block__filter-heading {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.program-list-block__filter-icon {
  flex-shrink: 0;
  color: var(--color-primary);
}

.program-list-block__filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 768px) {
  .program-list-block__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1200px) {
  .program-list-block__filters {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.program-list-block__label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
}

.program-list-block__search-wrap {
  position: relative;
}

.program-list-block__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}

.program-list-block__input,
.program-list-block__select {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-text);
  border-radius: 6px;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.program-list-block__input {
  padding: 0.625rem 1rem;
  background: var(--color-surface);
  border: 1px solid #d1d5db;
}

.program-list-block__select-wrap {
  position: relative;
  cursor: pointer;
}

.program-list-block__select {
  padding: 0.625rem 2.5rem 0.625rem 1rem;
  appearance: none;
  cursor: pointer;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
}

.program-list-block__select:hover,
.program-list-block__select:focus,
.program-list-block__select:focus-visible,
.program-list-block__select:active {
  cursor: pointer;
}

.program-list-block__select-chevron {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}

.program-list-block__input--search {
  padding-left: 2.5rem;
}

.program-list-block__input:focus,
.program-list-block__select:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.program-list-block__count {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.program-list-block__hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.program-list-block__hint--error {
  color: var(--color-error);
}

.program-list-block__map-details {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

@media (min-width: 640px) {
  .program-list-block__map-details {
    border-radius: var(--radius-lg);
  }
}

.program-list-block__map-summary {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  user-select: none;
}

.program-list-block__map-summary::-webkit-details-marker {
  display: none;
}

.program-list-block__map-summary::marker {
  content: '';
}

.program-list-block__map-summary-icon {
  flex-shrink: 0;
  color: var(--color-primary);
}

.program-list-block__map-summary-label {
  min-width: 0;
}

.program-list-block__map-summary-count {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-muted);
}

.program-list-block__map-chevron {
  margin-left: auto;
  flex-shrink: 0;
  color: var(--color-text-muted);
  transition: transform 0.2s ease;
}

.program-list-block__map-details[open] .program-list-block__map-chevron {
  transform: rotate(180deg);
}

.program-list-block__map-panel {
  border-top: 1px solid var(--color-border);
}

.program-list-block__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .program-list-block__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .program-list-block__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.program-list-block__card {
  display: block;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;
}

.program-list-block__card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.program-list-block__card:hover {
  border-color: color-mix(in srgb, var(--color-primary) 50%, var(--color-border));
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.08),
    0 8px 10px -6px rgba(0, 0, 0, 0.06);
  transform: translateY(-4px);
}

.program-list-block__card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.program-list-block__avatar {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--color-primary) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-primary);
  transition: background-color 0.3s ease;
}

.program-list-block__card:hover .program-list-block__avatar {
  background: color-mix(in srgb, var(--color-primary) 20%, transparent);
}

.program-list-block__badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.program-list-block__badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.program-list-block__badge--green {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}

.program-list-block__badge--blue {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.program-list-block__badge--purple {
  background: #f3e8ff;
  color: #7e22ce;
  border-color: #e9d5ff;
}

.program-list-block__badge--indigo {
  background: #e0e7ff;
  color: #4338ca;
  border-color: #c7d2fe;
}

.program-list-block__badge--neutral {
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  border-color: var(--color-border);
}

.program-list-block__card-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text);
  transition: color 0.3s ease;
}

.program-list-block__card:hover .program-list-block__card-title {
  color: var(--color-primary);
}

.program-list-block__meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-text-muted);
}

.program-list-block__meta-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.program-list-block__meta-text {
  min-width: 0;
}

.program-list-block__meta-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.program-list-block__details-wrap {
  margin-top: 1rem;
  padding: 0.5rem 0;
}

.program-list-block__details {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-primary);
  transition: color 0.2s ease;
}

.program-list-block__card:hover .program-list-block__details {
  color: color-mix(in srgb, var(--color-primary) 80%, var(--color-text));
}

.program-list-block__details-arrow {
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.program-list-block__card:hover .program-list-block__details-arrow {
  transform: translateX(4px);
}

.program-list-block__empty {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 3rem 1.5rem;
  text-align: center;
}

.program-list-block__empty-icon-wrap {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 9999px;
  background: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
}

.program-list-block__empty-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.program-list-block__empty-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}
</style>
